
import { useState, useRef, useEffect } from 'react';
import { Attendee } from '@/types/attendee';
import { updateAttendance } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatTShirtSize } from '@/utils/formatUtils';

// Define the expected return type from updateAttendance
interface UpdateAttendanceResult {
  success: boolean;
  data?: Attendee | null;
  error?: any;
}

export const useDirectDatabaseUpdate = (
  attendee: Attendee,
  onSuccess: (attendee: Attendee, timestamp: string) => void,
  onSilentUpdate?: (attendee: Attendee) => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const { toast } = useToast();
  
  // Set up safety timeouts and cleanup
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      
      // Clear any pending timeouts on unmount
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, []);
  
  const directDatabaseUpdate = async (code: string, updates: any) => {
    if (!code || isUpdating) return null;
    
    // Set a safety timeout to reset isUpdating after 8 seconds if the operation gets stuck
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        console.log('Force resetting isUpdating state after timeout in directDatabaseUpdate');
        setIsUpdating(false);
      }
    }, 8000);
    
    setIsUpdating(true);
    
    try {
      const timestamp = updates.attended ? new Date().toISOString() : null;
      // Ensure we explicitly track the t-shirt size from both sources
      const tShirtSize = updates.t_shirt_size || attendee.t_shirt_size;
      
      // Determine if this is an already checked-in attendee before update
      const wasAlreadyCheckedIn = attendee.attended;
      
      // Create a timeout promise to ensure the operation doesn't hang
      const timeoutPromise: Promise<UpdateAttendanceResult> = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database update timeout')), 7000);
      });
      
      // Call the database update function with a timeout
      const updatePromise: Promise<UpdateAttendanceResult> = updateAttendance(code, updates.attended, timestamp);
      
      // Race the database call against the timeout, explicitly typing the result
      const result = await Promise.race<UpdateAttendanceResult>([updatePromise, timeoutPromise])
        .catch(error => {
          console.error('Database update failed or timed out:', error);
          return { success: false, error: error.message } as UpdateAttendanceResult;
        });
      
      if (result && result.success && result.data) {
        // First create a baseline attendee object with required properties
        const baseAttendee: Partial<Attendee> = {
          ...attendee,
          ...result.data,
          attended: updates.attended,
          checkedInAt: updates.attended ? timestamp || undefined : undefined
        };
        
        // Create the updated attendee with t_shirt_size explicitly added
        // This addresses the TypeScript error by ensuring t_shirt_size is present
        const updatedAttendee: Attendee = {
          ...baseAttendee,
          // Explicitly add t_shirt_size to ensure it's present 
          // We use TypeScript type assertion here to solve the specific error
          t_shirt_size: tShirtSize || attendee.t_shirt_size
        } as Attendee;
        
        // Call the success callback with the updated attendee
        onSuccess(updatedAttendee, timestamp || '');
        
        // Call the silent update callback if provided
        if (onSilentUpdate) {
          onSilentUpdate(updatedAttendee);
        }
        
        // If check-in was successful and attendee was NOT already checked in, trigger event
        if (updates.attended && !wasAlreadyCheckedIn) {
          // Dispatch event but DO NOT use toast notification
          const checkedInEvent = new CustomEvent('attendee-checked-in', {
            detail: { 
              attendee: updatedAttendee,
              wasAlreadyCheckedIn: false,
              toastDisplayed: false, // Set to false to prevent toast in other components
              useModalOnly: true // Add flag to indicate we only want to use modal
            }
          });
          window.dispatchEvent(checkedInEvent);
        } else if (updates.attended && wasAlreadyCheckedIn) {
          // If attendee was already checked in, still dispatch event but with wasAlreadyCheckedIn flag
          const checkedInEvent = new CustomEvent('attendee-checked-in', {
            detail: { 
              attendee: updatedAttendee,
              wasAlreadyCheckedIn: true,
              useModalOnly: true
            }
          });
          window.dispatchEvent(checkedInEvent);
        }
        
        return updatedAttendee;
      }
      
      return null;
    } catch (error) {
      console.error('Error during direct database update:', error);
      return null;
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsUpdating(false);
      }
      
      // Clear the safety timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    }
  };
  
  return {
    isUpdating,
    directDatabaseUpdate
  };
};
